import { Chip, Paper, TextField, useTheme } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useEffect, useMemo, useContext, useState, useCallback, useRef } from "react";
import { isArray, isString } from "lodash";
import { Skeleton } from "@material-ui/lab";
import { TicketsContext } from "../../context/Tickets/TicketsContextIndexedDB";
import api from "../../services/api";

export function TagsContainer({ ticket }) {
    const theme = useTheme();
    const {
        tags,
        loadingTags,
        createTag,
        syncTicketTags,
        updateTicketTags
    } = useContext(TicketsContext);

    const [localTags, setLocalTags] = useState([]);
    const abortRef = useRef(null);

    const fetchTicketTags = useCallback(async (ticketId) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const { data } = await api.get(`/tickets/${ticketId}/tags`, {
                signal: controller.signal
            });
            const tagsList = Array.isArray(data?.tags) ? data.tags : (Array.isArray(data) ? data : []);
            setLocalTags(tagsList);
        } catch (err) {
            if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
                console.error("Error fetching ticket tags:", err);
            }
        }
    }, []);

    useEffect(() => {
        if (!ticket?.id) return;
        fetchTicketTags(ticket.id);
        return () => abortRef.current?.abort();
    }, [ticket?.id, fetchTicketTags]);

    // Memorizar las opciones de renderizado para el Autocomplete
    const renderOption = useMemo(() => (option) => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        }}>
            <div style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: option.color || '#2563eb'
            }} />
            <span>{option.name}</span>
        </div>
    ), []);

    // Memorizar el componente Paper para evitar re-renders innecesarios
    const PaperComponent = useMemo(() => ({ children }) => (
        <Paper style={{ 
            color: theme.palette.text.secondary, 
            backgroundColor: theme.palette.background.default 
        }}>
            {children}
        </Paper>
    ), [theme.palette.text.secondary, theme.palette.background.default]);

    // Memorizar el renderizado de los chips
    const renderTags = useMemo(() => (value, getTagProps) =>
        value.map((option, index) => (
            <Chip
                style={{
                    backgroundColor: option.color || '#2563eb',
                    color: 'white',
                    margin: '2px',
                    borderRadius: '9999px',
                    padding: 5
                }}
                label={option.name}
                {...getTagProps({ index })}
                size="small"
            />
        )), []);

    // Memorizar el componente de entrada
    const renderInput = useMemo(() => (params) => (
        <TextField
            {...params}
            variant="standard"
            placeholder="Seleccionar etiquetas existentes..."
            InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                style: { color: theme.palette.text.blackWhite }
            }}
        />
    ), [theme.palette.text.blackWhite]);

    const handleChange = async (e, value) => {
        let finalTags = value;
        
        if (isArray(value)) {
            const newTags = [];
            for (let item of value) {
                if (isString(item)) {
                    const newTag = await createTag({ name: item });
                    if (newTag) newTags.push(newTag);
                } else {
                    newTags.push(item);
                }
            }
            finalTags = newTags;
        }

        // Actualizar estado local inmediatamente
        setLocalTags(finalTags);
        
        // Actualizar el contexto
        updateTicketTags(ticket.id, finalTags);
        
        // Sincronizar con el backend
        await syncTicketTags(ticket.id, finalTags);
    };

    return (
        <div style={{ 
            padding: "6px 12px", 
            backgroundColor: theme.palette.background.default, 
            borderRadius: 12, 
            boxShadow: "none",
            border: `1px solid ${theme.palette.type === "light" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.08)"}`,
        }}>
            {loadingTags ? (
                <Skeleton variant="rect" width="100%" height={20} />
            ) : (
                <Autocomplete
                    multiple
                    size="small"
                    options={tags}
                    value={localTags}
                    freeSolo={false}
                    onChange={handleChange}
                    getOptionLabel={(option) => option.name}
                    getOptionSelected={(option, value) => option.id === value.id}
                    renderTags={renderTags}
                    renderInput={renderInput}
                    renderOption={renderOption}
                    PaperComponent={PaperComponent}
                    noOptionsText={"Sin opciones disponibles"}
                />
            )}
        </div>
    );
}